import { authAPI } from '../api';

export type AuthRole = 'user' | 'admin';
export type SubscriptionStatus = 'none' | 'active' | 'expired' | 'cancelled';
export type PlanType = 'none' | 'free' | 'pro' | 'pre-match' | 'live';
export type BillingCycle = 'none' | 'month' | 'week' | 'day-pass';

export interface AuthUser {
  id: string;
  name: string;
  label?: string;
  email: string;
  role: AuthRole;
  subscriptionStatus: SubscriptionStatus;
  plan: PlanType;
  planLabel?: string;
  billingCycle?: BillingCycle;
  billingManagedByStripe?: boolean;
  createdAt: string;
  updatedAt: string;
  lastLoginAt?: string | null;
  subscriptionEndsAt?: string | null;
  telegramAccess?: boolean;
  telegramLinked?: boolean;
  telegramUsername?: string | null;
  telegramAccessStatus?: string | null;
  telegramInviteLink?: string | null;
  communityAccess?: boolean;
  trackerAccess?: boolean;
  timezone?: string | null;
  language?: string | null;
}

export interface StoredAuthUser extends AuthUser {
  // Temporary only: passwords are stored locally in plain text so the app can
  // keep working without a backend. Replace this with hashed passwords and
  // real backend auth as soon as the API layer is introduced.
  password: string;
}

export interface RegisterUserInput {
  email: string;
  password: string;
  referralCode?: string;
}

export type AuthMode = 'local' | 'api';

export const AUTH_USERS_STORAGE_KEY = 'perfectodds_users';
export const AUTH_CURRENT_USER_STORAGE_KEY = 'perfectodds_current_user';
export const AUTH_API_TOKEN_STORAGE_KEY = 'perfectodds_access_token';
export const AUTH_MODE: AuthMode = process.env.REACT_APP_AUTH_MODE === 'api' ? 'api' : 'local';

const LEGACY_REGISTERED_USERS_KEY = 'perfectodds_registered_users';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const DEV_TIMESTAMP = '2026-05-21T00:00:00.000Z';
const PAID_PLANS = new Set<PlanType>(['pro', 'pre-match', 'live']);

export const shouldShowDevAuthHints = process.env.NODE_ENV !== 'production';

const DEV_SEED_USERS: StoredAuthUser[] = [
  {
    id: 'seed-test-user',
    name: 'Test gebruiker',
    email: 'test@perfectodds.nl',
    password: 'test123',
    role: 'user',
    subscriptionStatus: 'none',
    plan: 'none',
    createdAt: DEV_TIMESTAMP,
    updatedAt: DEV_TIMESTAMP,
  },
  {
    id: 'seed-free-user',
    name: 'Gratis groep',
    email: 'gratis@perfectodds.nl',
    password: 'test123',
    role: 'user',
    subscriptionStatus: 'none',
    plan: 'free',
    createdAt: DEV_TIMESTAMP,
    updatedAt: DEV_TIMESTAMP,
  },
  {
    id: 'seed-paid-user',
    name: 'Betalende klant',
    email: 'klant@perfectodds.nl',
    password: 'test123',
    role: 'user',
    subscriptionStatus: 'active',
    plan: 'pro',
    createdAt: DEV_TIMESTAMP,
    updatedAt: DEV_TIMESTAMP,
  },
  {
    id: 'seed-prematch-user',
    name: 'Pre-match klant',
    email: 'prematch@perfectodds.nl',
    password: 'test123',
    role: 'user',
    subscriptionStatus: 'active',
    plan: 'pre-match',
    createdAt: DEV_TIMESTAMP,
    updatedAt: DEV_TIMESTAMP,
  },
  {
    id: 'seed-live-user',
    name: 'Live klant',
    email: 'live@perfectodds.nl',
    password: 'test123',
    role: 'user',
    subscriptionStatus: 'active',
    plan: 'live',
    createdAt: DEV_TIMESTAMP,
    updatedAt: DEV_TIMESTAMP,
  },
  {
    id: 'seed-admin-user',
    name: 'Admin',
    email: 'admin@perfectodds.nl',
    password: 'admin123',
    role: 'admin',
    subscriptionStatus: 'active',
    plan: 'pro',
    createdAt: DEV_TIMESTAMP,
    updatedAt: DEV_TIMESTAMP,
  },
];

export const DEV_TEST_USERS = shouldShowDevAuthHints
  ? DEV_SEED_USERS.map(({ email, password, name, role, subscriptionStatus, plan }) => ({
      email,
      password,
      name,
      role,
      subscriptionStatus,
      plan,
    }))
  : [];

const readJson = <T,>(key: string, fallback: T): T => {
  try {
    const rawValue = window.localStorage.getItem(key);
    if (!rawValue) {
      return fallback;
    }
    return JSON.parse(rawValue) as T;
  } catch {
    return fallback;
  }
};

const normalizeEmail = (value: string) => value.trim().toLowerCase();

const normalizePlan = (value: unknown): PlanType => {
  if (value === 'free' || value === 'pro' || value === 'pre-match' || value === 'live' || value === 'none') {
    return value;
  }
  if (value === 'gratis') {
    return 'free';
  }
  return 'none';
};

const normalizeBillingCycle = (value: unknown): BillingCycle => {
  if (value === 'none' || value === 'month' || value === 'week' || value === 'day-pass') {
    return value;
  }
  return 'none';
};

const normalizeSubscriptionStatus = (value: unknown): SubscriptionStatus => {
  if (value === 'none' || value === 'active' || value === 'expired' || value === 'cancelled') {
    return value;
  }
  return value === 'active' ? 'active' : 'none';
};

const normalizeOptionalTimestamp = (value: unknown): string | null => {
  if (typeof value !== 'string' || !value) {
    return null;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
};

const resolveSubscriptionStatus = (
  role: AuthRole,
  subscriptionStatus: SubscriptionStatus,
  subscriptionEndsAt: string | null,
): SubscriptionStatus => {
  if (role === 'admin') {
    return subscriptionStatus;
  }

  if (subscriptionStatus === 'active' && subscriptionEndsAt && new Date(subscriptionEndsAt).getTime() <= Date.now()) {
    return 'expired';
  }

  return subscriptionStatus;
};

const normalizeRole = (value: unknown): AuthRole => (value === 'admin' ? 'admin' : 'user');

const createUserId = () => `user_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

const toStoredUser = (rawUser: Record<string, unknown>, index: number): StoredAuthUser => {
  const timestamp = typeof rawUser.updatedAt === 'string' ? rawUser.updatedAt : new Date().toISOString();
  const normalizedEmail = normalizeEmail(typeof rawUser.email === 'string' ? rawUser.email : `user-${index}@perfectodds.local`);
  const fallbackName = normalizedEmail.split('@')[0] || `Gebruiker ${index + 1}`;

  return {
    id: typeof rawUser.id === 'string' && rawUser.id ? rawUser.id : createUserId(),
    name:
      typeof rawUser.name === 'string' && rawUser.name.trim()
        ? rawUser.name.trim()
        : typeof rawUser.label === 'string' && rawUser.label.trim()
          ? rawUser.label.trim()
          : fallbackName,
    email: normalizedEmail,
    password: typeof rawUser.password === 'string' ? rawUser.password : '',
    role: normalizeRole(rawUser.role),
    subscriptionStatus: normalizeSubscriptionStatus(rawUser.subscriptionStatus),
    plan: normalizePlan(rawUser.plan),
    billingCycle: normalizeBillingCycle(rawUser.billingCycle),
    createdAt: typeof rawUser.createdAt === 'string' ? rawUser.createdAt : timestamp,
    updatedAt: timestamp,
    subscriptionEndsAt: normalizeOptionalTimestamp(rawUser.subscriptionEndsAt),
    telegramUsername: typeof rawUser.telegramUsername === 'string' ? rawUser.telegramUsername : null,
  };
};

const dedupeUsers = (users: StoredAuthUser[]): StoredAuthUser[] => {
  const uniqueUsers = new Map<string, StoredAuthUser>();
  users.forEach((user) => {
    uniqueUsers.set(normalizeEmail(user.email), user);
  });
  return Array.from(uniqueUsers.values());
};

const toAuthUser = (user: StoredAuthUser): AuthUser => {
  const subscriptionEndsAt = user.subscriptionEndsAt ?? null;
  const effectiveSubscriptionStatus = resolveSubscriptionStatus(user.role, user.subscriptionStatus, subscriptionEndsAt);
  const hasPaidAccess = user.role === 'admin' || (effectiveSubscriptionStatus === 'active' && PAID_PLANS.has(user.plan));

  return {
    id: user.id,
    name: user.name,
    email: user.email,
    role: user.role,
    subscriptionStatus: effectiveSubscriptionStatus,
    plan: user.plan,
    billingCycle: user.billingCycle ?? 'none',
    billingManagedByStripe: user.billingManagedByStripe ?? false,
    createdAt: user.createdAt,
    updatedAt: user.updatedAt,
    subscriptionEndsAt,
    telegramAccess: hasPaidAccess || user.role === 'admin',
    telegramUsername: user.telegramUsername ?? null,
    communityAccess: hasPaidAccess || user.role === 'admin',
    trackerAccess: hasPaidAccess || user.role === 'admin',
  };
};

export const normalizeAuthUser = (rawUser: Record<string, unknown>): AuthUser => {
  const createdAt = typeof rawUser.createdAt === 'string' ? rawUser.createdAt : new Date().toISOString();
  const updatedAt = typeof rawUser.updatedAt === 'string' ? rawUser.updatedAt : createdAt;
  const role = normalizeRole(rawUser.role);
  const subscriptionEndsAt = normalizeOptionalTimestamp(rawUser.subscriptionEndsAt);
  const subscriptionStatus = resolveSubscriptionStatus(
    role,
    normalizeSubscriptionStatus(rawUser.subscriptionStatus),
    subscriptionEndsAt,
  );
  const plan = normalizePlan(rawUser.plan);
  const billingCycle = normalizeBillingCycle(rawUser.billingCycle);

  return {
    id: typeof rawUser.id === 'number' ? String(rawUser.id) : typeof rawUser.id === 'string' ? rawUser.id : createUserId(),
    name: typeof rawUser.name === 'string' && rawUser.name.trim() ? rawUser.name.trim() : 'Gebruiker',
    email: normalizeEmail(typeof rawUser.email === 'string' ? rawUser.email : ''),
    role,
    subscriptionStatus,
    plan,
    billingCycle,
    billingManagedByStripe: rawUser.billingManagedByStripe === true,
    createdAt,
    updatedAt,
    lastLoginAt: typeof rawUser.lastLoginAt === 'string' ? rawUser.lastLoginAt : null,
    subscriptionEndsAt,
    telegramAccess: typeof rawUser.telegramAccess === 'boolean' ? rawUser.telegramAccess : role === 'admin' || (subscriptionStatus === 'active' && PAID_PLANS.has(plan)),
    telegramLinked: typeof rawUser.telegramLinked === 'boolean' ? rawUser.telegramLinked : false,
    telegramUsername: typeof rawUser.telegramUsername === 'string' ? rawUser.telegramUsername : null,
    telegramAccessStatus: typeof rawUser.telegramAccessStatus === 'string' ? rawUser.telegramAccessStatus : null,
    telegramInviteLink: typeof rawUser.telegramInviteLink === 'string' ? rawUser.telegramInviteLink : null,
    communityAccess: typeof rawUser.communityAccess === 'boolean' ? rawUser.communityAccess : role === 'admin' || (subscriptionStatus === 'active' && PAID_PLANS.has(plan)),
    trackerAccess: typeof rawUser.trackerAccess === 'boolean' ? rawUser.trackerAccess : role === 'admin' || (subscriptionStatus === 'active' && PAID_PLANS.has(plan)),
    timezone: typeof rawUser.timezone === 'string' ? rawUser.timezone : null,
    language: typeof rawUser.language === 'string' ? rawUser.language : null,
  };
};

const writeCurrentUser = (user: AuthUser | null) => {
  if (user) {
    window.localStorage.setItem(AUTH_CURRENT_USER_STORAGE_KEY, JSON.stringify(user));
  } else {
    window.localStorage.removeItem(AUTH_CURRENT_USER_STORAGE_KEY);
  }
};

const writeApiToken = (token: string | null) => {
  if (token) {
    window.localStorage.setItem(AUTH_API_TOKEN_STORAGE_KEY, token);
  } else {
    window.localStorage.removeItem(AUTH_API_TOKEN_STORAGE_KEY);
  }
};

const readApiToken = () => window.localStorage.getItem(AUTH_API_TOKEN_STORAGE_KEY);

export const saveStoredUsers = (users: StoredAuthUser[]) => {
  const normalizedUsers = dedupeUsers(users);
  window.localStorage.setItem(AUTH_USERS_STORAGE_KEY, JSON.stringify(normalizedUsers));
  window.localStorage.removeItem(LEGACY_REGISTERED_USERS_KEY);
};

export const getStoredUsers = (): StoredAuthUser[] => {
  const primaryUsers = readJson<StoredAuthUser[]>(AUTH_USERS_STORAGE_KEY, []);
  const legacyUsers = primaryUsers.length === 0 ? readJson<Record<string, unknown>[]>(LEGACY_REGISTERED_USERS_KEY, []) : [];
  const rawUsers = primaryUsers.length > 0 ? primaryUsers : legacyUsers;

  const normalizedUsers = dedupeUsers([
    ...DEV_SEED_USERS,
    ...rawUsers.map((user, index) => toStoredUser(user as Record<string, unknown>, index)),
  ]);

  saveStoredUsers(normalizedUsers);
  return normalizedUsers;
};

export const getCurrentUser = (): AuthUser | null => {
  const storedCurrentUser = readJson<Record<string, unknown> | null>(AUTH_CURRENT_USER_STORAGE_KEY, null);
  if (!storedCurrentUser) {
    return null;
  }

  const normalizedEmail = normalizeEmail(typeof storedCurrentUser.email === 'string' ? storedCurrentUser.email : '');
  const storedUsers = getStoredUsers();
  const matchingUser = storedUsers.find((user) => normalizeEmail(user.email) === normalizedEmail);

  if (matchingUser) {
    const authUser = toAuthUser(matchingUser);
    writeCurrentUser(authUser);
    return authUser;
  }

  const fallbackUser = toStoredUser(storedCurrentUser, 0);
  return toAuthUser(fallbackUser);
};

export const initializeAuthSession = async (): Promise<AuthUser | null> => {
  if (AUTH_MODE === 'local') {
    getStoredUsers();
    return getCurrentUser();
  }

  const token = readApiToken();
  if (!token) {
    writeCurrentUser(null);
    return null;
  }

  try {
    const response = await authAPI.getMe();
    const nextUser = normalizeAuthUser(response.data.user as Record<string, unknown>);
    writeCurrentUser(nextUser);
    return nextUser;
  } catch {
    writeApiToken(null);
    writeCurrentUser(null);
    return null;
  }
};

export const registerUser = async ({ email, password, referralCode }: RegisterUserInput): Promise<AuthUser> => {
  if (AUTH_MODE === 'api') {
    try {
      const response = await authAPI.register(email, password, referralCode);
      const nextUser = normalizeAuthUser(response.data.user as Record<string, unknown>);
      const token = typeof response.data.accessToken === 'string' ? response.data.accessToken : null;
      writeApiToken(token);
      writeCurrentUser(nextUser);
      return nextUser;
    } catch (error: unknown) {
      const message = typeof error === 'object' && error && 'response' in error
        ? (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : undefined;
      throw new Error(message || 'Registratie mislukt.');
    }
  }

  const normalizedEmail = normalizeEmail(email);
  const trimmedPassword = password.trim();

  if (!normalizedEmail || !trimmedPassword) {
    throw new Error('Vul alle velden in.');
  }

  if (!EMAIL_PATTERN.test(normalizedEmail)) {
    throw new Error('Vul een geldig e-mailadres in.');
  }

  if (trimmedPassword.length < 6) {
    throw new Error('Wachtwoord moet minimaal 6 tekens bevatten.');
  }

  const users = getStoredUsers();
  if (users.some((user) => normalizeEmail(user.email) === normalizedEmail)) {
    throw new Error('Er bestaat al een account met dit e-mailadres.');
  }

  const timestamp = new Date().toISOString();
  const accountName = normalizedEmail.split('@', 1)[0] || 'Gebruiker';
  const newUser: StoredAuthUser = {
    id: createUserId(),
    name: accountName,
    email: normalizedEmail,
    password: trimmedPassword,
    role: 'user',
    subscriptionStatus: 'none',
    plan: 'none',
    billingCycle: 'none',
    createdAt: timestamp,
    updatedAt: timestamp,
    subscriptionEndsAt: null,
    telegramUsername: null,
  };

  const nextUsers = [...users, newUser];
  saveStoredUsers(nextUsers);

  const currentUser = toAuthUser(newUser);
  writeCurrentUser(currentUser);
  return currentUser;
};

export const registerUserWithGoogle = async (credential: string, referralCode?: string): Promise<AuthUser> => {
  if (AUTH_MODE !== 'api') {
    throw new Error('Google-registratie is alleen beschikbaar met de live accountservice.');
  }

  try {
    const response = await authAPI.googleAuth(credential, true, referralCode);
    const nextUser = normalizeAuthUser(response.data.user as Record<string, unknown>);
    const token = typeof response.data.accessToken === 'string' ? response.data.accessToken : null;
    writeApiToken(token);
    writeCurrentUser(nextUser);
    return nextUser;
  } catch (error: unknown) {
    const message = typeof error === 'object' && error && 'response' in error
      ? (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
      : undefined;
    throw new Error(message || 'Registreren met Google is mislukt.');
  }
};

export const loginUserWithGoogle = async (credential: string): Promise<AuthUser> => {
  if (AUTH_MODE !== 'api') {
    throw new Error('Google-login is alleen beschikbaar met de live accountservice.');
  }

  try {
    const response = await authAPI.googleLogin(credential);
    const nextUser = normalizeAuthUser(response.data.user as Record<string, unknown>);
    const token = typeof response.data.accessToken === 'string' ? response.data.accessToken : null;
    writeApiToken(token);
    writeCurrentUser(nextUser);
    return nextUser;
  } catch (error: unknown) {
    const message = typeof error === 'object' && error && 'response' in error
      ? (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
      : undefined;
    throw new Error(message || 'Inloggen met Google is mislukt.');
  }
};

export const loginUser = async (email: string, password: string): Promise<AuthUser> => {
  if (AUTH_MODE === 'api') {
    try {
      const response = await authAPI.login(email, password);
      const nextUser = normalizeAuthUser(response.data.user as Record<string, unknown>);
      const token = typeof response.data.accessToken === 'string' ? response.data.accessToken : null;
      writeApiToken(token);
      writeCurrentUser(nextUser);
      return nextUser;
    } catch (error: unknown) {
      const message = typeof error === 'object' && error && 'response' in error
        ? (error as { response?: { data?: { detail?: string } } }).response?.data?.detail
        : undefined;
      throw new Error(message || 'Onjuiste e-mail of wachtwoord.');
    }
  }

  const normalizedEmail = normalizeEmail(email);
  const users = getStoredUsers();

  const user = users.find(
    (entry) => normalizeEmail(entry.email) === normalizedEmail && entry.password === password,
  );

  if (!user) {
    throw new Error('Onjuiste e-mail of wachtwoord.');
  }

  const currentUser = toAuthUser(user);
  writeCurrentUser(currentUser);
  return currentUser;
};

export const updateCurrentUser = (user: AuthUser): AuthUser => {
  const nextUser: AuthUser = {
    ...user,
    updatedAt: new Date().toISOString(),
  };

  if (AUTH_MODE === 'api') {
    writeCurrentUser(nextUser);
    return nextUser;
  }

  const users = getStoredUsers();
  const existingUser = users.find((entry) => entry.id === nextUser.id || normalizeEmail(entry.email) === normalizeEmail(nextUser.email));

  if (!existingUser) {
    throw new Error('Gebruiker niet gevonden.');
  }

  const updatedStoredUsers = users.map((entry) =>
    entry.id === existingUser.id
      ? {
          ...entry,
          ...nextUser,
          updatedAt: nextUser.updatedAt,
        }
      : entry,
  );

  saveStoredUsers(updatedStoredUsers);
  writeCurrentUser(nextUser);
  return nextUser;
};

export const logoutUser = () => {
  if (AUTH_MODE === 'api') {
    void authAPI.logout().catch(() => undefined);
    writeApiToken(null);
  }
  writeCurrentUser(null);
};

export const resetAuthDevData = () => {
  if (!shouldShowDevAuthHints) {
    return;
  }

  saveStoredUsers(DEV_SEED_USERS);
  logoutUser();
};

export const isLoggedIn = (user: AuthUser | null) => !!user;
export const isAdmin = (user: AuthUser | null) => user?.role === 'admin';
export const hasActiveSubscription = (user: AuthUser | null) => user?.subscriptionStatus === 'active';
export const canAccessTracker = (user: AuthUser | null) => {
  if (!user) {
    return false;
  }
  if (user.role === 'admin') {
    return true;
  }
  if (typeof user.trackerAccess === 'boolean') {
    return user.trackerAccess && hasActiveSubscription(user);
  }
  return hasActiveSubscription(user) && PAID_PLANS.has(user.plan);
};
export const canAccessAdmin = (user: AuthUser | null) => isAdmin(user);

export const getPlanLabel = (plan: PlanType, billingCycle: BillingCycle = 'none'): string => {
  if (plan === 'none') {
    return 'Geen actief abonnement';
  }

  if (plan === 'free') {
    return 'Gratis groep';
  }

  if (plan === 'pro' && billingCycle === 'day-pass') {
    return 'Pro 1-dagpas';
  }

  const baseLabels: Record<Exclude<PlanType, 'none' | 'free'>, string> = {
    pro: 'Pro abonnement',
    'pre-match': 'Pre-match abonnement',
    live: 'Live abonnement',
  };

  if (billingCycle === 'week') {
    return `${baseLabels[plan]} - week`;
  }

  if (billingCycle === 'month') {
    return `${baseLabels[plan]} - maand`;
  }

  return baseLabels[plan];
};

export const getDefaultRouteForUser = (user: AuthUser | null): string => {
  if (!user) {
    return '/login';
  }
  if (isAdmin(user)) {
    return '/admin';
  }
  if (hasActiveSubscription(user)) {
    return '/tracker';
  }
  return '/mijn-account';
};
